import {
  DemoIndicatorColoredIcon,
  OpenInNew1OutlineIcon,
  PlaybookIcon,
  PlusOutlineIcon,
  RecordingIndicatorColoredIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import {
  type DeeplinkAssets,
  PermissionAction,
  PermissionKey,
} from '@carboncopyco/types';
import { Button, Dropdown, Separator, TextBase } from '@carboncopyco/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Fragment, type ReactElement, useCallback, useMemo } from 'react';
import {
  useDesktopDeeplink,
  useModalActions,
  usePermissionsContext,
  useSearchParamAction,
} from '../utils';
import {
  type AssetMenuItemProps,
  CreateAssetMenuItem,
} from './CreateAssetMenuItem';

type CreateAssetMenuProps = {
  onCreateDemo: () => void;
  onCreateTour: () => void;
  onCreateClone: () => void;
  onCreatePlaybook: () => void;
  triggerElement?: ReactElement;
  asChild?: boolean;
  disabledItems?: ItemId[];
};
type ItemId = 'demo' | 'tour' | 'clone' | 'playbook';
type MenuConfigItem = AssetMenuItemProps & { id: ItemId };

export const CreateAssetMenu = ({
  triggerElement = (
    <Button
      label="Create new"
      leftIcon={<PlusOutlineIcon />}
    />
  ),
  onCreateDemo,
  onCreateTour,
  onCreateClone,
  onCreatePlaybook,
  asChild = true,
  disabledItems = [],
}: CreateAssetMenuProps) => {
  const flags = useFlags();
  const { getPermissions } = usePermissionsContext();
  const { exeDeeplink, shouldShowDeeplink } = useDesktopDeeplink();
  const { closeAllModals } = useModalActions();

  const createADemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.createADemo
  );

  const createTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.createTour
  );

  const createRecordingPermission = getPermissions(
    PermissionKey.record,
    PermissionAction.createRecording
  );

  const createPlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.createPlaybook
  );

  const getAssetHandleByType = useCallback((type: DeeplinkAssets) => {
    closeAllModals();
    switch (type) {
      case 'demo':
        onCreateDemo();
        break;
      case 'playbook':
        onCreatePlaybook();
        break;
      case 'clone':
        onCreateClone();
        break;
      default:
        break;
    }
  }, []);

  useSearchParamAction({
    param: 'create',
    action: (assetType: DeeplinkAssets) => {
      getAssetHandleByType(assetType);
    },
    expectedValueCallback: (value) =>
      ['playbook', 'demo', 'clone'].includes(value),
  });

  const isPlaybookDisabled = disabledItems.includes('playbook');
  const menuConfig: MenuConfigItem[] = useMemo(
    () => [
      {
        id: 'demo',
        permission: createADemoPermission,
        title: 'Demo',
        description: 'Add edits on a clone or your live app',
        icon: <DemoIndicatorColoredIcon size={32} />,
        onClick: () => exeDeeplink('create', 'demo', onCreateDemo),
      },
      {
        id: 'tour',
        permission: createTourPermission,
        title: 'Tour',
        description: 'Create interactive product tours',
        icon: <TourIndicatorColoredIcon size={32} />,
        onClick: onCreateTour,
      },
      {
        id: 'playbook',
        permission: flags.playbook && createPlaybookPermission,
        title: (
          <div className="flex items-center gap-1.5">
            <TextBase
              label="Playbook"
              color={
                isPlaybookDisabled
                  ? 'text-ds-natural-200'
                  : 'text-ds-natural-600'
              }
            />
          </div>
        ),
        description: 'Bundle existing demo assets and URLs',
        icon: isPlaybookDisabled ? (
          <div className="[&>svg]:opacity-25">
            <PlaybookIcon size={32} />
          </div>
        ) : (
          <PlaybookIcon size={32} />
        ),
        onClick: () =>
          exeDeeplink(
            'create',
            'playbook',

            onCreatePlaybook
          ),
        disabled: isPlaybookDisabled,
        tooltipText: isPlaybookDisabled
          ? 'You must have at least 1 Demo or Tour for creating a Playbook.'
          : undefined,
      },
      {
        id: 'clone',
        permission: createRecordingPermission,
        title: 'Clone',
        description: 'Spin up a demo or sandbox environment',
        icon: <RecordingIndicatorColoredIcon size={32} />,
        onClick: () => exeDeeplink('create', 'clone', onCreateClone),
      },
    ],
    [
      createADemoPermission,
      onCreateDemo,
      createTourPermission,
      onCreateTour,
      flags.playbook,
      createPlaybookPermission,
      onCreatePlaybook,
      createRecordingPermission,
      onCreateClone,
      isPlaybookDisabled,
    ]
  );

  return (
    <Dropdown
      asChild={asChild}
      align="center"
      width={330}
      triggerElement={triggerElement}
    >
      <div className="p-1.5">
        {menuConfig.map((item) => {
          if (item.id !== 'tour' && shouldShowDeeplink) {
            item.hoverTitle = <OpenInNew1OutlineIcon size={15} />;
          }
          if (item.id === 'clone') {
            return (
              <Fragment key={item.id}>
                <div className="my-1">
                  <Separator horizontal />
                </div>
                <CreateAssetMenuItem {...item} />
              </Fragment>
            );
          }
          return (
            <CreateAssetMenuItem
              key={item.id}
              {...item}
            />
          );
        })}
      </div>
    </Dropdown>
  );
};
